import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  approveVersion,
  createDocument,
  createDocumentType,
  createVersion,
  fetchDocument,
  fetchDocumentTypes,
  fetchDocuments,
  fetchVersions,
  markDocumentObsolete,
  markVersionObsolete,
  putVersionInForce,
  reviewVersion,
  submitVersionForReview,
  updateDocument,
  updateDocumentType,
} from './documentsApi';
import type {
  ControlledDocument,
  DocumentListParams,
  DocumentListResponse,
  DocumentPayload,
  DocumentStatus,
  DocumentType,
  DocumentTypePayload,
  DocumentVersion,
  AcknowledgementAudience,
  Pagination,
  VersionPayload,
} from './types';

interface DocumentsState {
  types: DocumentType[];
  typesLoading: boolean;
  items: ControlledDocument[];
  loading: boolean;
  pagination: Pagination;
  search: string;
  filterTypeId: string | '';
  filterStatus: DocumentStatus | '';
  current: ControlledDocument | null;
  versions: DocumentVersion[];
}

const initialState: DocumentsState = {
  types: [],
  typesLoading: false,
  items: [],
  loading: false,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  search: '',
  filterTypeId: '',
  filterStatus: '',
  current: null,
  versions: [],
};

export const loadTypes = createAsyncThunk<DocumentType[], boolean | undefined>(
  'documents/loadTypes',
  async (activeOnly) => fetchDocumentTypes(activeOnly ?? false),
);

export const createTypeThunk = createAsyncThunk<DocumentType, DocumentTypePayload>(
  'documents/createType',
  async (payload) => createDocumentType(payload),
);

export const updateTypeThunk = createAsyncThunk<
  DocumentType,
  { id: string; payload: Partial<DocumentTypePayload> }
>('documents/updateType', async ({ id, payload }) => updateDocumentType(id, payload));

export const loadDocuments = createAsyncThunk<DocumentListResponse, DocumentListParams | undefined>(
  'documents/loadDocs',
  async (params) => fetchDocuments(params ?? {}),
);

export const loadDocument = createAsyncThunk<ControlledDocument, string>(
  'documents/loadOne',
  async (id) => fetchDocument(id),
);

export const createDocumentThunk = createAsyncThunk<ControlledDocument, DocumentPayload>(
  'documents/createDoc',
  async (payload) => createDocument(payload),
);

export const updateDocumentThunk = createAsyncThunk<
  ControlledDocument,
  { id: string; payload: Partial<DocumentPayload> }
>('documents/updateDoc', async ({ id, payload }) => updateDocument(id, payload));

export const markObsoleteThunk = createAsyncThunk<ControlledDocument, string>(
  'documents/markObsolete',
  async (id) => markDocumentObsolete(id),
);

export const loadVersions = createAsyncThunk<DocumentVersion[], string>(
  'documents/loadVersions',
  async (documentId) => fetchVersions(documentId),
);

export const createVersionThunk = createAsyncThunk<
  DocumentVersion,
  { documentId: string; payload: VersionPayload }
>('documents/createVersion', async ({ documentId, payload }) => createVersion(documentId, payload));

export const submitForReviewThunk = createAsyncThunk<DocumentVersion, string>(
  'documents/submitReview',
  async (versionId) => submitVersionForReview(versionId),
);

export const reviewVersionThunk = createAsyncThunk<DocumentVersion, string>(
  'documents/reviewVersion',
  async (versionId) => reviewVersion(versionId),
);

export const approveVersionThunk = createAsyncThunk<DocumentVersion, string>(
  'documents/approveVersion',
  async (versionId) => approveVersion(versionId),
);

export const putInForceThunk = createAsyncThunk<
  DocumentVersion,
  { versionId: string; effectiveAt?: string | null; audience?: AcknowledgementAudience | null }
>('documents/putInForce', async ({ versionId, effectiveAt, audience }) =>
  putVersionInForce(versionId, { effectiveAt, audience }),
);

export const obsoleteVersionThunk = createAsyncThunk<DocumentVersion, string>(
  'documents/obsoleteVersion',
  async (versionId) => markVersionObsolete(versionId),
);

const slice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFilterTypeId(state, action: PayloadAction<string>) {
      state.filterTypeId = action.payload;
    },
    setFilterStatus(state, action: PayloadAction<DocumentStatus | ''>) {
      state.filterStatus = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    clearCurrent(state) {
      state.current = null;
      state.versions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTypes.pending, (state) => {
        state.typesLoading = true;
      })
      .addCase(loadTypes.fulfilled, (state, action) => {
        state.typesLoading = false;
        state.types = action.payload;
      })
      .addCase(loadTypes.rejected, (state) => {
        state.typesLoading = false;
      })
      .addCase(createTypeThunk.fulfilled, (state, action) => {
        state.types = [action.payload, ...state.types];
      })
      .addCase(updateTypeThunk.fulfilled, (state, action) => {
        state.types = state.types.map((t) => (t.id === action.payload.id ? action.payload : t));
      })
      .addCase(loadDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadDocuments.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadDocument.fulfilled, (state, action) => {
        state.current = action.payload;
        state.versions = action.payload.versions ?? [];
      })
      .addCase(createDocumentThunk.fulfilled, (state, action) => {
        const doc = action.payload;
        if (!doc.type && doc.typeId) {
          const type = state.types.find((t) => t.id === doc.typeId);
          if (type) {
            state.items = [{ ...doc, type: { id: type.id, code: type.code, name: type.name } }, ...state.items];
            return;
          }
        }
        state.items = [doc, ...state.items];
      })
      .addCase(updateDocumentThunk.fulfilled, (state, action) => {
        state.items = state.items.map((d) => (d.id === action.payload.id ? action.payload : d));
        if (state.current?.id === action.payload.id) {
          state.current = { ...state.current, ...action.payload };
        }
      })
      .addCase(markObsoleteThunk.fulfilled, (state, action) => {
        state.items = state.items.map((d) => (d.id === action.payload.id ? action.payload : d));
        if (state.current?.id === action.payload.id) {
          state.current = { ...state.current, ...action.payload };
        }
      })
      .addCase(loadVersions.fulfilled, (state, action) => {
        state.versions = action.payload;
      })
      .addCase(createVersionThunk.fulfilled, (state, action) => {
        state.versions = [action.payload, ...state.versions];
      })
      .addMatcher(
        (action) =>
          [
            submitForReviewThunk.fulfilled.type,
            reviewVersionThunk.fulfilled.type,
            approveVersionThunk.fulfilled.type,
            putInForceThunk.fulfilled.type,
            obsoleteVersionThunk.fulfilled.type,
          ].includes(action.type),
        (state, action: PayloadAction<DocumentVersion>) => {
          state.versions = state.versions.map((v) =>
            v.id === action.payload.id ? { ...v, ...action.payload } : v,
          );
        },
      );
  },
});

export const { setSearch, setFilterTypeId, setFilterStatus, setPage, clearCurrent } = slice.actions;
export default slice.reducer;
